import { createInterfaceToken } from '@bmai/shared';
import type {
    AdHocAggregateQuery,
    AdHocAggregateResult,
    AdHocRecordsQuery,
    AdHocRecordsResult,
    AdHocTrendQuery,
    AdHocTrendResult,
    AnalyticsFilter,
    AnomalyQuery,
    AnomalyResult,
    ConversationTurn,
    DirectoryUser,
    OlapTable,
    RePromptOverheadQuery,
    RePromptOverheadRow,
    RePromptTurn,
    RePromptTurnsQuery,
    UserActivityFrequency,
    UserActivityFrequencyQuery,
} from '@/shared/analytics/AnalyticsTypes';

/**
 * Gateway to the telemetry ad-hoc analytics query endpoint. Surfaces declarative
 * aggregation (group-by + metrics), time-series trends, raw record retrieval,
 * behavioral anomaly detection (session drift, declining activity), per-user
 * activity frequency (active days/weeks for engagement and consistency), and
 * per-session conversation transcripts over the OLAP `interactions` structure.
 */
export interface IAnalyticsGateway {
    aggregate(query: AdHocAggregateQuery): Promise<AdHocAggregateResult>;
    trend(query: AdHocTrendQuery): Promise<AdHocTrendResult>;
    records(query: AdHocRecordsQuery): Promise<AdHocRecordsResult>;
    /**
     * Runs a raw OLAP SQL string (the tile-config power-user fallback) and returns
     * the generic tabular result. `filters` travel as the declarative model �
     * telemetry compiles them into the leading `scoped` CTE � so a raw-SQL tile is
     * filter-reactive without composing SQL in the browser.
     */
    rawQuery(sql: string, filters?: AnalyticsFilter[]): Promise<OlapTable>;
    anomalies(query: AnomalyQuery): Promise<AnomalyResult>;
    userActivityFrequency(query: UserActivityFrequencyQuery): Promise<UserActivityFrequency[]>;
    rePromptOverhead(query: RePromptOverheadQuery): Promise<RePromptOverheadRow[]>;
    /** The individual re-prompt turns for one person — the repeated prompts behind their frustration signal. */
    rePromptTurns(query: RePromptTurnsQuery): Promise<RePromptTurn[]>;
    directoryUserIds(): Promise<string[]>;
    /** Full IDP-directory identities with org dimensions — the set used to fold never-active users into breakdowns. */
    directoryUsers(): Promise<DirectoryUser[]>;
    getSessionConversation(sessionId: string): Promise<ConversationTurn[]>;
}

export const IAnalyticsGatewayToken = createInterfaceToken<IAnalyticsGateway>('IAnalyticsGateway');
